import { useMediaQuery } from "@mantine/hooks"
import React from "react"
import { withNamespaces } from "react-i18next"
import { connect } from "react-redux"

import { selectUserLoggedIn } from "QuorumGrassroots/framework/selectors"
import { useCollaboratorAssignedBills } from "QuorumGrassroots/framework/components/CollaboratorBill/services"
import { Navigate, useNavigate } from "react-router-dom"
import LoadingSpinner from "app/static/frontend/components/LoadingSpinner"
import { DTVCard, DTVGrid, Icon } from "quorumdesign"

import { toDateTimeString } from "shared/imports/sharedHelperFunctions"

const SectionContainer = ({ isMobile, children }) => (
    <div
        style={{
            padding: "16px 20px 24px",
            border: "1px solid #ececec",
            borderRadius: "16px",
            boxShadow: "0 4px 12px -4px rgba(0, 0, 0, 0.2)",
            width: isMobile ? "100%" : "50%",
            backgroundColor: "white",
        }}
    >
        {children}
    </div>
)

const UnconnectedCollaboratorHomepage = ({ loggedIn }: { loggedIn: boolean }) => {
    const { data: apiData, isLoading, status } = useCollaboratorAssignedBills(loggedIn)
    const navigate = useNavigate()
    const showLoading = isLoading || status === "loading"

    const isMobile = useMediaQuery("(max-width: 720px)")

    if (!loggedIn) {
        return <Navigate to="/sign_in/" />
    }
    if (showLoading) {
        return <LoadingSpinner />
    }
    if ("forbidden" in apiData) {
        return <h1>You do not have access to this page.</h1>
    }

    const reviewedBills = apiData.filter((bill) => bill?.most_recent_comment)
    const unreviewedBills = apiData.filter((bill) => !bill?.most_recent_comment)

    return (
        <div
            style={{
                width: "100%",
                minHeight: isMobile ? "100%" : "calc(100vh - 100px)",
                backgroundColor: "#F1F3F5" /* Grey #1 from Quorum Theme */,
            }}
        >
            <div style={{ padding: isMobile ? "20px 25px 60px 25px" : "32px 40px 50px 40px" }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? "1rem" : "2rem",
                    }}
                >
                    <SectionContainer isMobile={isMobile}>
                        <DTVGrid
                            gridWidth={1}
                            headerText={
                                <>
                                    <Icon icon="bell" iconFamily="fal" size={isMobile ? "sm" : "md"} />
                                    <>Bills to Review ({unreviewedBills.length})</>
                                </>
                            }
                        >
                            {unreviewedBills && unreviewedBills.length > 0 ? (
                                unreviewedBills.map((bill) => (
                                    <DTVCard
                                        height="130px"
                                        onClick={() => navigate(`/bill/${bill.id}`)}
                                        title={bill.title}
                                        text={`${bill.current_status} on ${toDateTimeString(bill.current_status_date, null, true)}`}
                                        showCircleImage={false}
                                    />
                                ))
                            ) : (
                                <p>
                                    <i>No Unreviewed Bills</i>
                                </p>
                            )}
                        </DTVGrid>
                    </SectionContainer>
                    <SectionContainer isMobile={isMobile}>
                        <DTVGrid gridWidth={1} headerText={`Reviewed (${reviewedBills.length})`}>
                            {reviewedBills && reviewedBills.length > 0 ? (
                                reviewedBills.map((bill) => (
                                    <DTVCard
                                        height="130px"
                                        onClick={() => navigate(`/bill/${bill.id}`)}
                                        title={bill.title}
                                        text={
                                            <div>
                                                <span>
                                                    {bill.current_status} on{" "}
                                                    {toDateTimeString(bill.current_status_date, null, true)}
                                                </span>
                                                <p style={{ marginTop: 10, fontWeight: "500" }}>
                                                    <Icon
                                                        icon="check-circle"
                                                        iconFamily="far"
                                                        size="md"
                                                        color="green"
                                                        style={{ fontSize: "1em" }}
                                                    />
                                                    Commented on {toDateTimeString(bill.most_recent_comment)}
                                                </p>
                                            </div>
                                        }
                                        showCircleImage={false}
                                    />
                                ))
                            ) : (
                                <p>
                                    <i>No Reviewed Bills</i>
                                </p>
                            )}
                        </DTVGrid>
                    </SectionContainer>
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state, props) => ({
    loggedIn: selectUserLoggedIn(state),
})

export const CollaboratorHomepage = withNamespaces()(connect(mapStateToProps, null)(UnconnectedCollaboratorHomepage))
